import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CmrFieldPicker from "components/fde/CmrFieldPicker";
import { useTciRegistryOptions } from "components/fde/TciRegistrySelect";
import { submitCmrDataFlowRequest } from "services/fde/cmrDataFlowService";

/**
 * Institution-admin surface: propose CMR inbound/outbound for an institutional TCI.
 * Submits pending only — Global TCI Manager must approve before active.
 */
function InstitutionalCmrDataFlowPanel({ institutionId, disabled = false }) {
  const { tcis, loading, error: loadError, reload } = useTciRegistryOptions({
    institutionId,
    enabled: Boolean(institutionId),
  });
  const institutional = useMemo(
    () => (tcis || []).filter((row) => row.scope === "institutional"),
    [tcis],
  );

  const [tciId, setTciId] = useState("");
  const [inbound, setInbound] = useState([]);
  const [outbound, setOutbound] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const selected = useMemo(
    () => institutional.find((row) => row.id === tciId) || null,
    [institutional, tciId],
  );

  const onSelectTci = (nextId) => {
    setTciId(nextId);
    setError(null);
    setSuccess(null);
    const row = institutional.find((t) => t.id === nextId);
    const pending = row?.cmrDataFlowPending;
    const active = row?.cmrDataFlow;
    const source = pending || active || { inbound: [], outbound: [] };
    setInbound(Array.isArray(source.inbound) ? source.inbound : []);
    setOutbound(Array.isArray(source.outbound) ? source.outbound : []);
  };

  const onSubmit = async () => {
    if (!tciId) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await submitCmrDataFlowRequest({
        tciId,
        cmrDataFlow: { inbound, outbound },
      });
      setSuccess("SecureLink submitted for Global TCI Manager review.");
      await reload();
    } catch (err) {
      setError(err?.message || "Failed to submit SecureLink request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!institutionId) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        SecureLink (institutional TCIs)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Propose which MasterChart (CMR) fields this institutional TCI may read or write
        back via SecureLink. Changes stay pending until a Global TCI Manager approves them.
      </Typography>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <FormControl fullWidth disabled={disabled || loading}>
          <InputLabel id="inst-cmr-tci-label">Institutional TCI</InputLabel>
          <Select
            labelId="inst-cmr-tci-label"
            label="Institutional TCI"
            value={tciId}
            onChange={(event) => onSelectTci(event.target.value)}
          >
            {!institutional.length ? (
              <MenuItem disabled value="">
                No institutional TCIs yet
              </MenuItem>
            ) : (
              institutional.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.name || row.id}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {selected ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Status: ${selected.cmrDataFlowStatus || "none"}`}
              color={
                selected.cmrDataFlowStatus === "active"
                  ? "success"
                  : selected.cmrDataFlowStatus === "pending"
                    ? "warning"
                    : selected.cmrDataFlowStatus === "rejected"
                      ? "error"
                      : "default"
              }
            />
            {selected.cmrDataFlowRejectionReason ? (
              <Chip
                size="small"
                color="error"
                label={`Rejected: ${selected.cmrDataFlowRejectionReason}`}
              />
            ) : null}
          </Stack>
        ) : null}

        <CmrFieldPicker
          mode="inbound"
          tciId={tciId}
          value={inbound}
          onChange={setInbound}
          disabled={disabled || !tciId || selected?.cmrDataFlowStatus === "pending"}
        />
        <CmrFieldPicker
          mode="outbound"
          tciId={tciId}
          value={outbound}
          onChange={setOutbound}
          disabled={disabled || !tciId || selected?.cmrDataFlowStatus === "pending"}
        />

        <LoadingButton
          variant="contained"
          loading={submitting}
          disabled={
            disabled ||
            !tciId ||
            selected?.cmrDataFlowStatus === "pending" ||
            (!inbound.length && !outbound.length)
          }
          onClick={onSubmit}
        >
          Submit for Global TCI Manager review
        </LoadingButton>
      </Stack>
    </Box>
  );
}

InstitutionalCmrDataFlowPanel.propTypes = {
  institutionId: PropTypes.string,
  disabled: PropTypes.bool,
};

export default InstitutionalCmrDataFlowPanel;
